import { addMonths, eachDayOfInterval, endOfMonth, format, startOfDay } from 'date-fns';
import type { EnhancedDeal, ProjectTask, TimberProject } from '../types';
import type { InstallWeatherSiteInput } from './installWeather';
import { allDayCalendarDate } from './allDayDate';

/** One scheduled install row on the People team calendar (backed by a project installation task). */
export type PeopleCalProjectInstall = {
  /** projectTasks doc id */
  taskId: string;
  projectId: string;
  projectName: string;
  taskTitle: string;
};

function eligibleContractedPipelineProject(p: TimberProject, dealById: Map<string, EnhancedDeal>): boolean {
  if (!p.id || !p.dealId) return false;
  const deal = dealById.get(p.dealId);
  if (!deal || deal.outcome === 'lost' || deal.pmFinalizedAt) return false;
  if (p.phase === 'preconstruction') return false;
  if (p.status === 'completed') return false;
  return true;
}

function isInstallScheduleTask(t: ProjectTask): boolean {
  return t.department === 'installation';
}

/**
 * Maps YYYY-MM-DD → install chips for the visible calendar range.
 * Each installation task appears on every scheduled day from `startDate` through `endDate`
 * (inclusive), so multi-day installs span the full range on the team calendar.
 */
export function buildProjectInstallsByYmd(
  dealById: Map<string, EnhancedDeal>,
  projects: TimberProject[],
  tasks: ProjectTask[],
  rangeStartMonth: Date,
  monthsVisible: number,
): Map<string, PeopleCalProjectInstall[]> {
  const rangeStart0 = startOfDay(rangeStartMonth);
  const rangeEnd = endOfMonth(addMonths(rangeStartMonth, monthsVisible - 1));
  const tMin = rangeStart0.getTime();
  const tMax = startOfDay(rangeEnd).getTime();

  const out = new Map<string, PeopleCalProjectInstall[]>();

  for (const p of projects) {
    if (!eligibleContractedPipelineProject(p, dealById)) continue;
    const projectTasks = tasks.filter(
      t => t.projectId === p.id && isInstallScheduleTask(t) && t.id && t.startDate && t.endDate,
    );
    for (const t of projectTasks) {
      if (!t.id) continue;
      const start = allDayCalendarDate(t.startDate);
      const end = allDayCalendarDate(t.endDate);
      if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end < start) continue;

      const clipStart = new Date(Math.max(start.getTime(), tMin));
      const clipEnd = new Date(Math.min(end.getTime(), tMax));
      if (clipEnd < clipStart) continue;

      const row: PeopleCalProjectInstall = {
        taskId: t.id,
        projectId: p.id!,
        projectName: p.name || 'Project',
        taskTitle: t.title || 'Install',
      };

      eachDayOfInterval({ start: clipStart, end: clipEnd }).forEach(day => {
        const key = format(day, 'yyyy-MM-dd');
        const list = out.get(key) ?? [];
        list.push(row);
        out.set(key, list);
      });
    }
  }

  out.forEach(list => {
    list.sort((a, b) => {
      const pn = a.projectName.localeCompare(b.projectName);
      if (pn !== 0) return pn;
      return a.taskTitle.localeCompare(b.taskTitle);
    });
  });

  return out;
}

/** YYYY-MM-DD keys from an installs map (ES5-safe; no Map/Set iteration). */
export function listInstallYmdsFromMap(installsByYmd: Map<string, PeopleCalProjectInstall[]>): string[] {
  const ymds: string[] = [];
  installsByYmd.forEach((_list, ymd) => {
    ymds.push(ymd);
  });
  return ymds;
}

/** Site inputs for install weather forecast from visible calendar installs. */
export function buildInstallWeatherSitesFromInstallMap(
  installsByYmd: Map<string, PeopleCalProjectInstall[]>,
  projects: TimberProject[],
): InstallWeatherSiteInput[] {
  const projectIds: string[] = [];
  installsByYmd.forEach(list => {
    for (let i = 0; i < list.length; i++) {
      const pid = list[i].projectId;
      if (projectIds.indexOf(pid) === -1) {
        projectIds.push(pid);
      }
    }
  });

  const sites: InstallWeatherSiteInput[] = [];
  for (let i = 0; i < projectIds.length; i++) {
    const pid = projectIds[i];
    for (let j = 0; j < projects.length; j++) {
      const p = projects[j];
      if (!p.id || p.id !== pid) continue;
      sites.push({
        projectId: p.id,
        coordinates: p.coordinates,
        location: p.location,
        address: p.address,
      });
      break;
    }
  }
  return sites;
}
